// components/fournisseurs/SuppliersList.tsx
import { useState, useEffect } from 'react';
import { createResource,getResources,deleteResource, updateResource, patchResource} from '../../../router/api';
import { Toaster } from 'react-hot-toast';

interface Supplier {
  id: string;
  reference: string;
  raisonSociale: string;
  nif: string;
  contactPrincipal: string;
  telephone: string;
  email: string;
  adresse: string;
  conditionsPaiement: string;
  devise: string;
  delaiMoyenLivraison: number;
  actif: boolean;
  bloque: boolean;
  motif_blocage?: string;
}

interface SuppliersListProps {
  onEdit: (supplier: Supplier) => void;
  onViewDetail: (supplier: Supplier) => void;
  onViewHistory: (supplier: Supplier) => void;
  onCreateOrder: (supplier: Supplier) => void;
}

export default function SuppliersList({
  onEdit,
  onViewDetail,
  onViewHistory,
  onCreateOrder,
}: SuppliersListProps) {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState<Supplier[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('Tous');
  const [loading, setLoading] = useState(true);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await getResources('fournisseurs');

      const data = response.data.data || response.data;
      setSuppliers(data);
      setFilteredSuppliers(data);
    } catch (err) {
      console.error('Erreur chargement fournisseurs', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  useEffect(() => {
    let filtered = suppliers;

    if (searchTerm) {
      filtered = filtered.filter(s =>
        s.raisonSociale.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.contactPrincipal.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'Tous') {
      if (filterStatus === 'Actif') {
        filtered = filtered.filter(s => s.actif && !s.bloque);
      } else {
        filtered = filtered.filter(s => !s.actif || s.bloque);
      }
    }

    setFilteredSuppliers(filtered);
  }, [searchTerm, filterStatus, suppliers]);

  const getStatusDisplay = (supplier: Supplier) => {
    if (supplier.bloque) {
      return { text: 'Bloqué', color: 'bg-red-100 text-red-800' };
    }
    if (supplier.actif) {
      return { text: 'Actif', color: 'bg-green-100 text-green-800' };
    }
    return { text: 'Inactif', color: 'bg-gray-100 text-gray-800' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <i className="ri-loader-4-line animate-spin text-4xl text-blue-600"></i>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">

      <Toaster position="bottom-right" />

      {/* Filtres */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              placeholder="Rechercher par nom, référence ou contact..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          <div className="sm:w-48">
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="Tous">Tous les statuts</option>
              <option value="Actif">Actif</option>
              <option value="Inactif">Inactif / Bloqué</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="relative overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Fournisseur
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">
                Conditions
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">
                Performance
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Statut
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {filteredSuppliers.map(supplier => {
              const status = getStatusDisplay(supplier);

              return (
                <tr key={supplier.id} className="hover:bg-gray-50">
                  {/* Fournisseur */}
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-gray-900">
                        {supplier.raisonSociale}
                      </div>
                      <div className="text-xs text-gray-500">
                        Réf: {supplier.reference}
                      </div>
                      <div className="text-xs text-gray-500">
                        NIF: {supplier.nif}
                      </div>
                    </div>
                  </td>

                  {/* Contact */}
                  <td className="px-6 py-4 hidden md:table-cell">
                    <div className="space-y-1 text-sm">
                      <div>{supplier.contactPrincipal}</div>
                      <div className="text-gray-500">{supplier.telephone}</div>
                      <div className="text-gray-500">{supplier.email}</div>
                    </div>
                  </td>

                  {/* Conditions */}
                  <td className="px-6 py-4 hidden lg:table-cell">
                    <div className="text-sm">
                      <div>{supplier.conditionsPaiement}</div>
                      <div className="text-gray-500">
                        Devise: {supplier.devise}
                      </div>
                    </div>
                  </td>

                  {/* Performance */}
                  <td className="px-6 py-4 hidden lg:table-cell">
                    <div className="text-sm">
                      Délai moyen: {supplier.delaiMoyenLivraison} jours
                    </div>
                  </td>

                  {/* Statut */}
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${status.color}`}>
                      {status.text}
                      {supplier.motif_blocage && supplier.bloque && (
                        <span className="ml-1 opacity-70">
                          ({supplier.motif_blocage})
                        </span>
                      )}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => onViewDetail(supplier)} className="p-2 hover:bg-blue-50 rounded">
                        <i className="ri-eye-line text-blue-600"></i>
                      </button>
                      <button onClick={() => onEdit(supplier)} className="p-2 hover:bg-blue-50 rounded">
                        <i className="ri-edit-line text-blue-600"></i>
                      </button>
                      <button onClick={() => onViewHistory(supplier)} className="p-2 hover:bg-green-50 rounded">
                        <i className="ri-history-line text-green-600"></i>
                      </button>
                      {/* <button onClick={() => onCreateOrder(supplier)} className="p-2 hover:bg-purple-50 rounded">
                        <i className="ri-shopping-cart-line text-purple-600"></i>
                      </button> */}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Aucun résultat */}
      {filteredSuppliers.length === 0 && (
        <div className="text-center py-12">
          <i className="ri-inbox-line text-4xl text-gray-400 mb-4"></i>
          <p className="text-gray-500">Aucun fournisseur trouvé</p>
        </div>
      )}
    </div>
  );
}
